import { useMemo } from "react";
import { observer } from "mobx-react";
import { Tables, hashCode } from "@repo/utils";
import {
  useEstimateStructure,
  usePresetFieldNames,
  useEstimateEditorSettings,
  useTableExpandHandler,
  EstimateTableSettingsProvider,
  TableTitle,
  ScrollButtonContainer,
} from "@tools";
import { TableContainer, Grid, Collapse } from "@material-ui/core";
import { TableHeader } from "./components/TableHeader/TableHeader";
import { BudgetTrackingSummaryRowGroup } from "./components/BudgetTrackingSummaryRowGroup/BudgetTrackingSummaryRowGroup";
import useStyle from "@tools/EstimateEditor/styles/CommonTableStyles.style";
import classnames from "classnames";
import { isMobile } from "react-device-detect";

export const BudgetTrackingSummary = observer(() => {
  const structure = useEstimateStructure();
  const classes = useStyle();
  const { isProposal, blockExpansions } = useEstimateEditorSettings();
  const { getFieldName } = usePresetFieldNames(isProposal);

  const { settings, btWorkTypes } = structure;
  
  const {showSummary, visibleSummaryColumns } = settings;

  const columns = useMemo(() => (
    visibleSummaryColumns.filter((column) => ["cost", "hours", "rate"].includes(column.name))
  ), [visibleSummaryColumns]);

  const tableContainerIdentifier = useMemo(() => "summaryContainer", []);
  const tableIdentifier = useMemo(() => "summaryHeader", []);

  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: true,
    isOpened: showSummary,
    onExpand: settings.setShowSummary
  });

  return (
    <EstimateTableSettingsProvider
      useExpandCell
      columns={columns}
    >
      <Grid
        item
        container
        justifyContent="center"
        alignItems="center"
        className={classnames(
          "preset-paper",
          classes.whiteContainer,
        )}
      >
        <Grid item container justifyContent="flex-end">
          <TableTitle
            expandable={!blockExpansions}
            presetClass="summary"
            id={hashCode(Tables.SUMMARY)}
            title={getFieldName("pt-summary")}
            showContent={showSummary}
            onContentAction={handleOpen}
          />
          <Collapse
            in={showSummary}
            timeout="auto"
            mountOnEnter
            unmountOnExit
            className={classnames(classes.collapse)}
            data-id={hashCode("cont")}
            data-t={ Tables.SUMMARY }
            data-p={hashCode(Tables.SUMMARY)}
            {...collapseProps}
          >
            <Grid
              container
              justifyContent="center"
              wrap="nowrap"
              direction="column"
            >
              {
                isTableContentOverflowing && !isMobile &&
                <ScrollButtonContainer {...scrollButtonProps} />
              }
              <TableContainer
                ref={ref}
                id={tableContainerIdentifier}
                onScroll={onScroll}
                className={classnames(
                  "pb-3",
                  classes.tableContainer,
                  isTableContentOverflowing && classes.overflows
                )}
              >
                <Grid container role="rowgroup" direction="column">
                  <TableHeader tableIdentifier={tableIdentifier}/>
                  {btWorkTypes.map((workType) => (
                    <BudgetTrackingSummaryRowGroup
                      key={workType.id}
                      workType={workType}
                    />
                  ))}
                </Grid>
              </TableContainer>
            </Grid>
          </Collapse>
        </Grid>
      </Grid>
    </EstimateTableSettingsProvider>
  );
});